// The small conversions the Promotions tab's dialogs need between what an operator types and what the
// contract carries (spec/06 §8.4–§8.5): a percentage ↔ basis points, a clock time ↔ a minute of the
// venue's day, a calendar date ↔ an RFC 3339 instant at the venue, and a promotion's status in words.
// No floats: "12.5" % is split at the decimal point, like a price (`../money.ts`).
//
// The venue-clock conversions use `Intl` directly rather than `@clavian/domain`'s: the domain package
// pulls the contracts' zod schemas into the bundle, and their `eval` breaks the console's CSP
// (`pages-csp.test.ts`).

import type { Money } from '@clavian/api-client';

/** "10" → 1000, "12.5" → 1250, "0.25" → 25; `null` for anything else, or outside (0, 100]. */
export function percentToBps(input: string): number | null {
  const match = /^(\d{1,3})(?:\.(\d{1,2}))?$/.exec(input.trim().replace(/%$/, '').trim());
  if (match === null) return null;
  const bps = Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0') || '0');
  return bps >= 1 && bps <= 10_000 ? bps : null;
}

/** 1250 → "12.5", 1000 → "10". */
export function bpsToPercent(bps: number): string {
  const whole = Math.trunc(bps / 100);
  const fraction = String(bps % 100).padStart(2, '0').replace(/0+$/, '');
  return fraction === '' ? String(whole) : `${whole}.${fraction}`;
}

/** "17:30" → 1050; `null` unless it is a clock time. */
export function minuteOfDay(time: string): number | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(time.trim());
  return match === null ? null : Number(match[1]) * 60 + Number(match[2]);
}

/** 1050 → "17:30". */
export function timeOfMinute(minute: number): string {
  return `${String(Math.trunc(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
}

/** The venue's wall clock at an instant: `[year, month, day, hour, minute, second]`. */
function wallClock(millis: number, timezone: string): number[] {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(millis));
  const part = (type: string) => Number(parts.find((one) => one.type === type)?.value ?? '0');
  return [part('year'), part('month'), part('day'), part('hour'), part('minute'), part('second')];
}

/** The instant the venue's clock reads `date` at `secondOfDay`: a UTC guess, corrected by the zone's offset twice (DST). */
function instantAt(date: string, secondOfDay: number, timezone: string): number {
  const [y = 1970, m = 1, d = 1] = date.split('-').map(Number);
  const wanted = Date.UTC(y, m - 1, d) + secondOfDay * 1000;
  let guess = wanted;
  for (let pass = 0; pass < 2; pass += 1) {
    const [wy = 1970, wm = 1, wd = 1, wh = 0, wmin = 0, ws = 0] = wallClock(guess, timezone);
    guess += wanted - Date.UTC(wy, wm - 1, wd, wh, wmin, ws);
  }
  return guess;
}

/** A date an operator picked, as the instant the venue's day starts (a "valid from"). */
export function startOfDayIso(date: string, timezone: string): string {
  return new Date(instantAt(date, 0, timezone)).toISOString();
}

/** A date an operator picked, as the last second of the venue's day (a "valid until", inclusive). */
export function endOfDayIso(date: string, timezone: string): string {
  return new Date(instantAt(date, 86_399, timezone)).toISOString();
}

/** An instant, as the venue's calendar date — what a date field shows. */
export function venueDateOf(iso: string, timezone: string): string {
  const [y = 1970, m = 1, d = 1] = wallClock(Date.parse(iso), timezone);
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export type OfferStatus = 'active' | 'paused' | 'scheduled' | 'ended' | 'usedUp';

/** Where a promotion, code or batch stands now: off, not started, over, used up, or running. */
export function offerStatus(
  offer: { readonly active: boolean; readonly validFrom: string | null; readonly validUntil: string | null },
  now: number,
  usedUp = false,
): OfferStatus {
  if (!offer.active) return 'paused';
  if (offer.validUntil !== null && Date.parse(offer.validUntil) < now) return 'ended';
  if (usedUp) return 'usedUp';
  if (offer.validFrom !== null && Date.parse(offer.validFrom) > now) return 'scheduled';
  return 'active';
}

export const STATUS_TONE: Readonly<Record<OfferStatus, 'ok' | 'neutral' | 'info' | 'warn'>> = {
  active: 'ok',
  paused: 'neutral',
  scheduled: 'info',
  ended: 'neutral',
  usedUp: 'warn',
};

/**
 * An offer's state as the rail at its edge (ADR-0043): running is settled green, used up wants a look, paused or over is
 * quiet; a scheduled offer is plain until it starts.
 */
export const STATUS_RAIL: Readonly<Record<OfferStatus, 'ok' | 'warn' | 'quiet' | undefined>> = {
  active: 'ok',
  paused: 'quiet',
  scheduled: undefined,
  ended: 'quiet',
  usedUp: 'warn',
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** A console offer's reward and conditions, as the contract carries them (ADR-0034). */
export interface ConsoleOfferTerms {
  readonly reward: 'extra_controllers_percent' | 'extra_controllers_amount' | 'bundle' | 'free_time' | 'session_percent';
  readonly percentBps: number | null;
  readonly amount: Money | null;
  readonly controllers: number | null;
  readonly minutes: number | null;
  readonly freeMinutes: number | null;
  readonly minControllers: number | null;
  readonly minDurationMinutes: number | null;
  readonly stationClassIds: readonly string[];
  readonly playersOnly: boolean;
}

/** "2 h", "30 min", "1 h 30 min". */
export function minutesWords(t: Translate, minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return t('consoleSession.minutes', { n: rest });
  return rest === 0 ? t('consoleSession.hours', { n: hours }) : t('consoleSession.hoursMinutes', { h: hours, m: rest });
}

/**
 * A console offer's reward in words (ADR-0034): "50% off extra controllers", "₹50.00 off each extra controller",
 * "4 controllers × 2 h for ₹500.00", "Play 2 h, get 30 min free", "10% off the session" — with its cap.
 */
export function consoleOfferWords(t: Translate, money: (value: Money) => string, offer: ConsoleOfferTerms, cap: Money | null): string {
  const percent = offer.percentBps === null ? '—' : bpsToPercent(offer.percentBps);
  const amount = offer.amount === null ? '—' : money(offer.amount);
  let value: string;
  switch (offer.reward) {
    case 'extra_controllers_percent':
      value = t('pricing.console.value.extraPercent', { percent });
      break;
    case 'extra_controllers_amount':
      value = t('pricing.console.value.extraAmount', { amount });
      break;
    case 'bundle':
      value = t('pricing.console.value.bundle', { controllers: offer.controllers ?? '—', time: offer.minutes === null ? '—' : minutesWords(t, offer.minutes), amount });
      break;
    case 'free_time':
      value = t('pricing.console.value.freeTime', {
        paid: offer.minutes === null ? '—' : minutesWords(t, offer.minutes),
        free: offer.freeMinutes === null ? '—' : minutesWords(t, offer.freeMinutes),
      });
      break;
    case 'session_percent':
      value = t('pricing.console.value.sessionPercent', { percent });
      break;
  }
  return cap === null ? value : t('pricing.value.cappedSession', { value, cap: money(cap) });
}

/** A console offer's conditions as short phrases (its day, window and venues are the promotion's own). */
export function consoleConditionWords(t: Translate, offer: ConsoleOfferTerms): string[] {
  const words: string[] = [t('pricing.kind.console')];
  if (offer.minControllers !== null) words.push(t('pricing.console.rules.minControllers', { count: offer.minControllers }));
  if (offer.minDurationMinutes !== null) words.push(t('pricing.console.rules.minDuration', { time: minutesWords(t, offer.minDurationMinutes) }));
  if (offer.stationClassIds.length > 0) words.push(t('pricing.console.rules.classes', { count: offer.stationClassIds.length }));
  if (offer.playersOnly) words.push(t('pricing.console.rules.playersOnly'));
  return words;
}

/**
 * A discount in words: "10% off, up to ₹200.00", "₹50.00 off". A play-time promotion's cap is per session
 * ("20% off, up to ₹100.00 a session"), since play is discounted unit by unit as it is played.
 */
export function valueWords(
  t: Translate,
  money: (value: Money) => string,
  type: 'percent' | 'amount',
  percentBps: number | null,
  amount: Money | null,
  cap: Money | null,
  perSession = false,
): string {
  const value = type === 'percent' ? t('pricing.value.percent', { percent: percentBps === null ? '—' : bpsToPercent(percentBps) }) : t('pricing.value.amount', { amount: amount === null ? '—' : money(amount) });
  if (cap === null) return value;
  return t(perSession ? 'pricing.value.cappedSession' : 'pricing.value.capped', { value, cap: money(cap) });
}
